import { useEffect, useMemo, useState } from "react";
import { useMatch, useLocation, useNavigate } from "react-router-dom";

// formik components
import { Formik, Form } from "formik";

// Auth0
import { useAuth0 } from "@auth0/auth0-react";

// PropTypes
import PropTypes from "prop-types";

// Global controller
import { useSoftUIController } from "hooks/context";

// @mui material components
import { Card, Stepper, Step, StepLabel } from "@mui/material";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";

import Swal from "sweetalert2";

// NewUser layout schemas for form and form feilds
import form from "./schemas/form";
import validationSchema from "./schemas/validationSchema";
import defaultServiceValues from "./schemas/initialValues";

import TechnicianActions from "./wizards/TechnicianActions";
import AdditionalNotes from "./wizards/AdditionalNotes";
import Review from "./wizards/Review";

function getStepContent(stepIndex, deviceLayout, formData) {
    switch (stepIndex) {
        case 0:
            return <TechnicianActions formData={formData} deviceLayout={deviceLayout} />;
        case 1:
            return <AdditionalNotes formData={formData} />;
        case 2:
            return <Review formData={formData} />;
        default:
            return null;
    }
}

function determineInspectionStatus(inspection_data) {
    for (var device of inspection_data.devices) {
        if (device.conditions.other.length > 0 || device.actions.other.length > 0) {
            return "review";
        }
    }

    return "resolved";
}

function AssignmentForm({ selected_user, all_devices, inspection_record, onSubmit }) {
    const navigate = useNavigate();
    const { user } = useAuth0();
    const [controller] = useSoftUIController();
    const { deviceLayout } = controller;

    const [deviceOptions, setDeviceOptions] = useState([]);
    const [formType, setFormType] = useState("");
    const [activeStep, setActiveStep] = useState(0);
    const steps = ["Maintenance/Repairs", "Notes & Images", "Review"];

    const { formId, formField } = form;
    const currentValidation = validationSchema[activeStep] || Yup.object().shape({});//[(formType != '' && activeStep) ? formType : 0];
    const isLastStep = activeStep === steps.length - 1;// && typeof formType != "string";

    const handleSubmit = (values, actions) => {
        // console.log(values, actions);
        if (isLastStep) {
            submitForm(values, actions);
        } else {
            setActiveStep(activeStep + 1);
            actions.setTouched({});
            actions.setSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const handleBack = () => setActiveStep(activeStep - 1);

    function getObjValues(values, label) {
        var ret = {};
        var label_values = Object.keys(values).filter((row) => row.includes(label));

        for (var el of label_values) {
            var element_arr = el.split('-');
            var type = element_arr[(element_arr.length - 1)];
            ret[type] = values[`${label}-${type}`];
        }

        return ret;
    }

    const submitForm = async (values, actions) => {
        console.log(values, actions);
        // status can be "review", "resolved"

        // rectification has description, technician, creation time (createdAt), devices and images ATTACHED to original inspection
        // items that will be updated on the ORIGINAL inspection are: status



        var formData = {
            to_update: {},
            metadata: {
                inspection_record_id: inspection_record._id,
                organisation_id: inspection_record.organisation_id,
            },
            contents: {
                title: values.title == "" ? values.inspection_type : values.title,
                description: values.description,
                technician: user.nickname.split('.')[0],
                devices: [],
            }
        };

        for (var deviceIndex = 0; deviceIndex < 2; deviceIndex++) {
            var deviceLabel = deviceIndex == 0 ? 'devicePrimary' : 'deviceSecondary';

            if (values[`${deviceLabel}-info`] != "") {

                values[`${deviceLabel}-images`].forEach((image, imageIndex) => {
                    formData[`device${deviceIndex}_image${imageIndex}`] = image.data;
                });

                formData.contents.devices[deviceIndex] = {
                    uuid: values[`${deviceLabel}-uuid`],
                    name: values[`${deviceLabel}-info`].label,
                    serial_no: values[`${deviceLabel}-serial_no`],
                    stop_id: values[`${deviceLabel}-stop_id`],
                    conditions: getObjValues(values, `${deviceLabel}-conditions`),
                    actions: getObjValues(values, `${deviceLabel}-actions`),
                    images: values[`${deviceLabel}-images`].map((image, imageIndex) => {
                        return {
                            key: `device${deviceIndex}_image${imageIndex}`,
                            previewUrl: image.previewUrl
                        }
                    })
                }
            }
        }

        formData.to_update.status = determineInspectionStatus(formData.contents);

        formData.to_update = JSON.stringify(formData.to_update);
        formData.metadata = JSON.stringify(formData.metadata);
        formData.contents = JSON.stringify(formData.contents);

        try {
            Swal.fire({
                icon: "warning",
                title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Validating...</div>`,
                html: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif;">Processing inspection data and images...</div>',
                allowOutsideClick: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            const result = await onSubmit(formData);
            console.log("Submission successful:", result);

            await Swal.fire({
                icon: "success",
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Saved!</div>',
                showConfirmButton: false,
                timer: 2000,
            });

            navigate(-1);

        } catch (err) {
            console.error("Submission failed:", err);
            const errorMessage = /*err.message || */"Something went wrong while saving.";

            actions.setSubmitting(false);

            await Swal.fire({
                icon: "error",
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error!</div>',
                html: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">${errorMessage}</div>`,
                showConfirmButton: true,
                timer: 15000,
            });
        }
    }

    const formInitialValues = useMemo(() => {
        if (!inspection_record || !inspection_record.devices || !inspection_record.devices[0]) {
            return defaultServiceValues;
        }

        const primary = inspection_record.devices[0];
        let initial = { ...defaultServiceValues };

        // Map Basic Info
        initial['devicePrimary-uuid'] = primary.uuid || "";
        initial['devicePrimary-serial_no'] = primary.serial_no || "";
        initial['devicePrimary-stop_id'] = primary.stop_id || "";
        initial['devicePrimary-info'] = { label: primary.name };

        // Map Conditions
        if (primary.conditions) {
            Object.keys(primary.conditions).forEach(key => {
                initial[`devicePrimary-conditions-${key}`] = primary.conditions[key];
            });
        }

        // Map Actions
        if (primary.actions) {
            Object.keys(primary.actions).forEach(key => {
                initial[`devicePrimary-actions-${key}`] = primary.actions[key];
            });
        }

        return initial;
    }, [inspection_record]);

    return (
        <SoftBox px={1} pb={1} py={deviceLayout == "mobile" ? 1 : "1rem"}>
            {deviceLayout != "mobile" && (
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            )}
            <Formik
                initialValues={formInitialValues}
                validationSchema={currentValidation}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={handleSubmit}>
                {({ values, errors, touched, isSubmitting, setFieldValue, handleChange }) => {
                    Object.keys(errors).length > 0 && console.log("Validation Errors:", errors);

                    return (
                        <Form id={formId} autoComplete="off">
                            <Card sx={{ overflow: "visible", height: '100%' }}>
                                <SoftBox p={3} height="100%">
                                    {getStepContent(activeStep, deviceLayout, {
                                        values,
                                        touched,
                                        errors,
                                        isSubmitting,
                                        formField,
                                        functions: {
                                            setDeviceOptions,
                                            setFormType,
                                            setFieldValue,
                                            handleChange,
                                            handleBack,
                                            handleCancel
                                        },
                                        custom: {
                                            activeStep,
                                            isLastStep,
                                            user: selected_user,
                                            inspection_record,
                                            all_devices,
                                            deviceOptions
                                        }
                                    })}
                                </SoftBox>
                            </Card>
                        </Form>
                    )
                }}
            </Formik>
        </SoftBox>
    );
}

AssignmentForm.propTypes = {
    selected_user: PropTypes.object.isRequired,
    all_devices: PropTypes.array.isRequired,
    inspection_record: PropTypes.object,
    onSubmit: PropTypes.func,
};

export default AssignmentForm;